/**
 * Metabase dashboard API types.
 *
 * GENERATED FILE - DO NOT EDIT BY HAND.
 *
 * Vendored from metabase/metabase, `frontend/src/metabase-types/api/dashboard.ts` and everything it
 * transitively references, flattened into a single module with no internal
 * imports (the upstream modules are mutually circular).
 *
 * Upstream commit: 12834520193bcc56b6ab6493373fabba64c6b8c3
 * Extracted:       2026-09-14
 *
 * Regenerate with:
 *
 * ```
 * npx tsx src/lib/metabase/scripts/sync-types.ts
 * ```
 *
 * See ./../README.md for the deliberate deviations from upstream.
 */

/* eslint-disable tsdoc/syntax */

// ----------------------------------------------------------------------------
// stub for `JSONContent` from `@tiptap/core`
// ----------------------------------------------------------------------------

/**
 * Upstream this is `JSONContent` from `@tiptap/core`, which the Hub does not
 * depend on. Structurally compatible for the purposes of these types.
 */
export type JSONContent = {
  type?: string;
  content?: JSONContent[];
  [key: string]: unknown;
};

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/dashboard.ts
// ----------------------------------------------------------------------------

// x-ray dashboard have string ids
export type DashboardId = number | string;

export type DashboardCard = ActionDashboardCard | QuestionDashboardCard | VirtualDashboardCard;

export type DashboardWidth = "full" | "fixed";

export interface Dashboard {
  id: DashboardId;
  entity_id: BaseEntityId;
  created_at: string;
  creator_id: UserId;
  creator?: UserInfo;
  updated_at: string;
  collection?: Collection | null;
  collection_id: CollectionId | null;
  name: string;
  description: string | null;
  model?: string;
  dashcards: DashboardCard[];
  tabs?: DashboardTab[];
  show_in_getting_started?: boolean | null;
  parameters?: Parameter[] | null;
  point_of_interest?: string | null;
  collection_authority_level?: CollectionAuthorityLevel;
  can_write: boolean;
  can_restore: boolean;
  can_delete: boolean;
  can_set_cache_policy?: boolean;
  cache_ttl: number | null;
  "last-edit-info": {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    timestamp: string;
  };
  last_used_param_values: Record<ParameterId, string | number | boolean | null | string[] | number[]>;
  auto_apply_filters: boolean;
  archived: boolean;
  is_remote_synced?: boolean;
  public_uuid: string | null;
  initially_published_at: string | null;
  embedding_params?: EmbeddingParameters | null;
  width: DashboardWidth;
  param_fields?: Record<ParameterId, Field[]>;

  moderation_reviews: ModerationReview[];
  view_count?: number;

  /* Indicates whether static embedding for this dashboard has been published */
  enable_embedding: boolean;
  embedding_type?: EmbeddingType | null;

  /* For x-ray dashboards */
  transient_name?: string;
  related?: RelatedDashboardXRays;
  more?: string | null;
}

export type RelatedDashboardXRays = {
  related?: RelatedDashboardXRayItem[];
  "zoom-in"?: RelatedDashboardXRayItem[];
  "zoom-out"?: RelatedDashboardXRayItem[];
  compare?: RelatedDashboardXRayItem[];
};

export type RelatedDashboardXRayItem = {
  description: string;
  title: string;
  url: string;
};

/** Dashboards with string ids, like x-rays, cannot have cache configurations */
export type CacheableDashboard = Omit<Dashboard, "id"> & { id: number };

export type DashboardQueryMetadata = {
  databases: Database[];
  tables: Table[];
  fields: Field[];
  cards: Card[];
  dashboards: Dashboard[];
};

export type DashCardId = number;

export type DashboardCardLayoutAttrs = {
  col: number;
  row: number;
  size_x: number;
  size_y: number;
};

export type DashboardCardPosition = Pick<DashboardCardLayoutAttrs, "col" | "row">;

export type DashboardCardSize = Pick<DashboardCardLayoutAttrs, "size_x" | "size_y">;

export type DashCardVisualizationSettings = {
  [key: string]: unknown;
  virtual_card?: VirtualCard;
  iframe?: string;
  column_settings?: Record<string, ColumnSettings>;
};

export type BaseDashboardCard = DashboardCardLayoutAttrs & {
  id: DashCardId;
  dashboard_id: DashboardId;
  dashboard_tab_id: DashboardTabId | null;
  card_id: CardId | null;
  card: Card | VirtualCard;
  collection_authority_level?: CollectionAuthorityLevel;
  entity_id: BaseEntityId;
  visualization_settings?: DashCardVisualizationSettings;
  justAdded?: boolean;
  created_at: string;
  updated_at: string;
};

export type VirtualCard = Partial<Omit<Card, "name" | "dataset_query" | "visualization_settings" | "display">> & {
  name: null;
  dataset_query?: Record<string, never>; // Some old virtual cards have dataset_query equal to {}
  display: VirtualCardDisplay;
  visualization_settings: VisualizationSettings;
};

export type ActionDashboardCard = Omit<BaseDashboardCard, "parameter_mappings"> & {
  action_id: WritebackActionId;
  action?: WritebackAction;
  card_id: CardId | null; // model card id for the associated action
  card: Card;

  parameter_mappings?: ActionParametersMapping[] | null;
  visualization_settings: DashCardVisualizationSettings & {
    "button.label"?: string;
    click_behavior?: ClickBehavior;
    actionDisplayType?: ActionDisplayType;
    virtual_card: VirtualCard;
  };
};

export type QuestionDashboardCard = BaseDashboardCard & {
  card_id: CardId | null; // will be null for virtual card
  card: Card;
  inline_parameters: ParameterId[] | null;
  parameter_mappings?: DashboardParameterMapping[] | null;
  series?: Card[];
};

export type VisualizerDashboardCard = QuestionDashboardCard & {
  visualization_settings: BaseDashboardCard["visualization_settings"] & {
    visualization: VisualizerVizDefinition;
  };
};

export type VirtualDashboardCard = BaseDashboardCard & {
  card_id: null;
  card: VirtualCard;
  inline_parameters: ParameterId[] | null;
  parameter_mappings?: VirtualDashCardParameterMapping[] | null;
  visualization_settings: BaseDashboardCard["visualization_settings"] & {
    virtual_card: VirtualCard;
    link?: LinkCardSettings;
    text?: string;
  };
};

export type DashboardTabId = number;

export type DashboardTab = {
  id: DashboardTabId;
  dashboard_id: DashboardId;
  entity_id?: BaseEntityId;
  name: string;
  position?: number;
  created_at?: string;
  updated_at?: string;
};

export type DashboardParameterMapping = {
  card_id: CardId;
  parameter_id: ParameterId;
  target: ParameterTarget;
};

export type ActionParametersMapping = Pick<DashboardParameterMapping, "parameter_id" | "target">;

export type VirtualDashCardParameterMapping = {
  parameter_id: ParameterId;
  target: ParameterTarget;
};

export type DashCardDataMap = Record<DashCardId, Record<CardId, Dataset | null | undefined>>;

export type LinkEntity = RestrictedLinkEntity | UnrestrictedLinkEntity;

export type UnrestrictedLinkEntity = {
  id: number;
  db_id?: number;
  database_id?: number;
  model: SearchModel;
  name: string;
  display_name?: string;
  description?: string | null;
  display?: CardDisplayType;
};

export type RestrictedLinkEntity = {
  restricted: true;
};

export interface LinkCardSettings {
  url?: string;
  entity?: LinkEntity;
}

export interface GetCompatibleCardsPayload {
  last_cursor?: number;
  limit: number;
  query?: string;
  exclude_ids: number[];
}

export type ListDashboardsRequest = {
  f?: "all" | "mine" | "archived";
};

// GET /api/dashboard endpoint does not hydrate all Dashboard attributes
export type ListDashboardsResponse = Omit<
  Dashboard,
  "dashcards" | "tabs" | "collection" | "collection_authority_level" | "can_write" | "param_fields"
>[];

export type GetDashboardRequest = {
  id: DashboardId;
  ignore_error?: boolean;
  dashboard_load_id?: string;
};

export type DashboardParameterValuesRequest = {
  dashId?: DashboardId | EntityToken;
  entityIdentifier?: EntityUuid | EntityToken | null;
  paramId: ParameterId;
};

export type SearchDashboardParameterValuesRequest = DashboardParameterValuesRequest & {
  query: string;
};

export type CreateDashboardRequest = {
  name: string;
  description?: string | null;
  parameters?: Parameter[] | null;
  cache_ttl?: number;
  collection_id?: CollectionId | null;
  collection_position?: number | null;
  tabs?: Pick<DashboardTab, "id" | "name" | "position">[];
};

export type UpdateDashboardRequest = {
  id: DashboardId;
  collection_position?: number | null;
  caveats?: string | null;
  position?: number | null;
} & Partial<
  Pick<
    Dashboard,
    | "parameters"
    | "point_of_interest"
    | "description"
    | "archived"
    | "dashcards"
    | "tabs"
    | "show_in_getting_started"
    | "enable_embedding"
    | "embedding_type"
    | "collection_id"
    | "name"
    | "width"
    | "embedding_params"
    | "cache_ttl"
  >
>;

export type GetDashboardQueryMetadataRequest = {
  id: DashboardId;
  dashboard_load_id?: string;
};

export type DashboardCardQueryRequest = {
  dashboardId: DashboardId;
  dashcardId: DashCardId;
  cardId: CardId;
  collection_preview?: boolean;
  ignore_cache?: boolean;
  parameters?: unknown[];
  // Sent in the request body (in addition to the path params above) when
  // querying a saved dashcard, for query provenance/telemetry.
  dashboard_id?: DashboardId;
  dashboard_load_id?: string;
};

export type SaveDashboardRequest = Omit<UpdateDashboardRequest, "id">;

export type CopyDashboardRequest = {
  id: DashboardId;
  name?: string | null;
  description?: string | null;
  collection_id?: CollectionId | null;
  collection_position?: number | null;
  is_deep_copy?: boolean | null;
};

export type UpdateDashboardPropertyRequest<Key extends keyof UpdateDashboardRequest> = Required<
  Pick<UpdateDashboardRequest, "id" | Key>
>;

export type GetPublicDashboard = Pick<Dashboard, "id" | "name" | "public_uuid">;

export type GetEmbeddableDashboard = Pick<Dashboard, "id" | "name">;

/** The subset of a Metabase dashboard that is safe to show a dataset viewer. */
export interface DatasetDashboardInfo {
  name: string | null;
  description: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export type GetRemappedDashboardParameterValueRequest = {
  dashId?: DashboardId;
  entityIdentifier?: EntityUuid | EntityToken | null;
  paramId: ParameterId;
  value: ParameterValueOrArray;
};

export type GetValidDashboardFilterFieldsRequest = {
  filtered: FieldId[];
  filtering: FieldId[];
};

export type DashCardSeriesItem = {
  card: Card | VirtualCard;
  isSlow: boolean;
  isUsuallyFast: boolean;
} & Partial<Dataset>;

export type DashCardSeries = DashCardSeriesItem[];

export type DashCardDataSeriesItem = SingleSeries<Card> &
  Dataset & {
    isSlow: boolean;
    isUsuallyFast: boolean;
  };

export type DashCardDataSeries = DashCardDataSeriesItem[];

export type VisualizerSeriesItem = Pick<SingleSeries, "card"> &
  Partial<Omit<SingleSeries, "card">> & {
    _isVisualizer: true;
  };

export type VisualizerSeries = VisualizerSeriesItem[];

export type VisualizerDataSeriesItem = SingleSeries & {
  _isVisualizer: true;
};

export type VisualizerDataSeries = VisualizerDataSeriesItem[];

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/actions.ts
// ----------------------------------------------------------------------------

export interface WritebackParameter extends Parameter {
  target: ParameterTarget;
}

export type WritebackActionId = number;

export interface WritebackActionBase {
  id: WritebackActionId;
  model_id: CardId;
  name: string;
  description: string | null;
  parameters?: WritebackParameter[];
  visualization_settings?: ActionFormSettings;
  archived: boolean;
  creator_id: UserId;
  creator: UserInfo;
  updated_at: string;
  created_at: string;
  public_uuid: string | null;
  database_id?: DatabaseId;
  database_enabled_actions?: boolean;
  entity_id: BaseEntityId;
}

export interface QueryAction {
  type: "query";
  dataset_query: NativeDatasetQuery;
}

export interface ImplicitQueryAction {
  type: "implicit";
  kind: "row/create" | "row/update" | "row/delete";
}

export interface HttpAction {
  type: "http";
  template: HttpActionTemplate;
  response_handle: string | null;
  error_handle: string | null;
}

export interface HttpActionTemplate {
  method: string;
  url: string;
  body: string;
  headers: string;
  parameters: Record<ParameterId, Parameter>;
  parameter_mappings: Record<ParameterId, ParameterTarget>;
}

export type WritebackAction = WritebackActionBase & (QueryAction | ImplicitQueryAction | HttpAction);

export type ActionDisplayType = "form" | "button";

export type FieldType = "string" | "number" | "date";

export type DateInputType = "date" | "time" | "datetime";

// these types are saved in visualization_settings
export type InputSettingType = DateInputType | "string" | "text" | "number" | "select" | "radio" | "boolean";

export type Size = "small" | "medium" | "large";

export type DateRange = [string, string];

export type NumberRange = [number, number];

export type FieldValueOptions = (string | number)[];

export interface FieldSettings {
  id: string;
  name: string;
  title: string;
  order: number;
  description?: string | null;
  placeholder?: string;
  fieldType: FieldType;
  inputType: InputSettingType;
  required: boolean;
  defaultValue?: string | number;
  hidden: boolean;
  range?: DateRange | NumberRange;
  valueOptions?: FieldValueOptions;
  width?: Size;
  height?: number;
  hasSearch?: boolean;
}

export type FieldSettingsMap = Record<ParameterId, FieldSettings>;

export interface ActionFormSettings {
  name?: string;
  type?: ActionDisplayType;
  description?: string;
  fields?: FieldSettingsMap;
  submitButtonLabel?: string;
  submitButtonColor?: string;
  confirmMessage?: string;
  successMessage?: string;
  errorMessage?: string;
}

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/card.ts
// ----------------------------------------------------------------------------

export const CARD_TYPES = ["model", "question", "metric"] as const;

export type CardType = (typeof CARD_TYPES)[number];

export type CardCreationType = "simple_question" | "custom_question" | "native_question";

export type CardDashboardInfo = Pick<Dashboard, "id" | "name">;

export type CardDocumentInfo = Pick<Document, "id" | "name">;

export interface Card<Q extends DatasetQuery = DatasetQuery> extends UnsavedCard<Q> {
  id: CardId;
  entity_id: BaseEntityId;
  created_at: string;
  updated_at: string;
  name: string;
  description: string | null;
  type: CardType;
  public_uuid: string | null;

  /* Indicates whether static embedding for this card has been published */
  enable_embedding: boolean;
  embedding_type?: EmbeddingType | null;
  embedding_params: EmbeddingParameters | null;
  can_write: boolean;
  can_restore: boolean;
  can_delete: boolean;
  can_manage_db: boolean;
  initially_published_at: string | null;

  database_id?: DatabaseId;
  collection?: Collection | null;
  collection_id: CollectionId | null;
  collection_position: number | null;
  dashboard: CardDashboardInfo | null;
  dashboard_id: DashboardId | null;
  document_id?: DocumentId | null;
  document?: CardDocumentInfo | null;
  metabot_conversation_id?: string | null;
  metabot_chart_id?: string | null;
  dashboard_count: number | null;
  parameter_usage_count?: number | null;

  result_metadata: Field[] | null;
  param_fields?: Record<ParameterId, Field[]>;
  moderation_reviews?: ModerationReview[];
  persisted?: boolean;

  query_average_duration?: number | null;
  last_query_start: string | null;
  average_query_time: number | null;
  cache_ttl: number | null;
  based_on_upload?: TableId | null; // table id of upload table, if any

  archived: boolean;
  is_remote_synced?: boolean;

  creator?: UserInfo;
  "last-edit-info"?: LastEditInfo;
  table_id?: TableId;
  view_count?: number;

  download_perms?: DownloadPermission;
  displayIsLocked?: boolean;
}

export interface UnsavedCard<Q extends DatasetQuery = DatasetQuery> {
  display: VisualizationDisplay;
  dataset_query: Q;
  parameters?: Parameter[];
  visualization_settings: VisualizationSettings;

  // If coming from dashboard
  dashboardId?: DashboardId;
  dashcardId?: DashCardId;

  // Not part of the card API contract, a field used by query builder for showing lineage
  original_card_id?: number;
  displayIsLocked?: boolean;

  // Not part of the card API contract, a transient marker for how the card was created
  creationType?: CardCreationType;
}

export type LineSize = "S" | "M" | "L";

export type SeriesSettings = {
  title?: string;
  color?: string;
  /** Palette color the series was given, so it can follow the active palette */
  color_name?: string;
  show_series_values?: boolean;
  display?: VisualizationDisplay;
  axis?: string;
  "line.size"?: LineSize;
  "line.style"?: "solid" | "dashed" | "dotted";
  "line.interpolate"?: string;
  "line.marker_enabled"?: boolean;
  "line.missing"?: string;
  show_series_trendline?: boolean;
};

export type SeriesOrderSetting = {
  name: string;
  key: string;
  enabled: boolean;
  color?: string;
};

export type ConditionalFormattingCommonOperator = "is-null" | "not-null";

export type ConditionalFormattingComparisonOperator = "=" | "!=" | "<" | ">" | "<=" | ">=";

export type ConditionalFormattingStringOperator =
  | "="
  | "!="
  | "contains"
  | "does-not-contain"
  | "starts-with"
  | "ends-with";

export type ConditionalFormattingBooleanOperator = "is-true" | "is-false";

export type ColumnFormattingOperator =
  | ConditionalFormattingCommonOperator
  | ConditionalFormattingComparisonOperator
  | ConditionalFormattingStringOperator
  | ConditionalFormattingBooleanOperator;

export type ColumnSingleFormattingSetting = {
  columns: string[];
  type: "single";
  operator: ColumnFormattingOperator;
  color: string;
  highlight_row: boolean;
  value: RowValue;
};

export type ColumnRangeFormattingSetting = {
  columns: string[];
  type: "range";
  colors: string[];
  min_type: "custom" | "all" | null;
  max_type: "custom" | "all" | null;
  min_value?: number;
  max_value?: number;
};

export type ColumnFormattingSetting = ColumnSingleFormattingSetting | ColumnRangeFormattingSetting;

export type ColumnNameColumnSplitSetting = {
  rows: string[];
  columns: string[];
  values: string[];
};

export type FieldRefColumnSplitSetting = {
  rows: (FieldReference | null)[];
  columns: (FieldReference | null)[];
  values: (FieldReference | null)[];
};

// Field ref-based visualization settings are considered legacy and are not used
// for new questions. To not break existing questions we need to support both
// old- and new-style settings until they are fully migrated.
export type PivotTableColumnSplitSetting = ColumnNameColumnSplitSetting | FieldRefColumnSplitSetting;

export type ColumnNameCollapsedRowsSetting = {
  rows: string[];
  value: string[]; // identifiers for collapsed rows
};

export type FieldRefCollapsedRowsSetting = {
  rows: (FieldReference | null)[];
  value: string[];
};

export type PivotTableCollapsedRowsSetting = ColumnNameCollapsedRowsSetting | FieldRefCollapsedRowsSetting;

export type TableColumnOrderSetting = {
  name: string;
  enabled: boolean;
};

export type StackType = "stacked" | "normalized" | null;

export type StackValuesDisplay = "total" | "all" | "series";

export type StackValueFormat = "value" | "percentage";

export const numericScale = ["linear", "pow", "log"] as const;

export type NumericScale = (typeof numericScale)[number];

export type BoxPlotWhiskerType = "tukey" | "min-max";

export type BoxPlotPointsMode = "none" | "outliers" | "all";

export type BoxPlotShowValuesMode = "median" | "all";

export type XAxisScale = "ordinal" | "histogram" | "timeseries" | NumericScale;

export type YAxisScale = NumericScale;

export type MapType = "region" | "pin" | "heat" | "grid";

export type PinMapStyle = "tiles" | "markers" | "grid" | "heat";

export type ColumnSettings = TimeOnlyOptions & {
  _column_title_full?: string;
  "pivot_table.column_show_totals"?: boolean;
  text_align?: "left" | "middle" | "right";
  click_behavior?: ClickBehavior;
  clicked?: any;
  collapseNewlines?: boolean;
  column?: any;
  column_title?: string;
  compact?: boolean;
  currency?: string;
  currency_style?: CurrencyStyle;
  date_abbreviate?: boolean;
  date_format?: string;
  date_separator?: string;
  date_style?: string | null;
  decimals?: number;
  isExclude?: boolean;
  jsx?: boolean;
  link_text?: string;
  link_url?: string;
  majorWidth?: number;
  markdown_template?: any;
  maximumFractionDigits?: number;
  negativeInParentheses?: boolean;
  noRange?: boolean;
  number_separators?: string;
  number_style?: string;
  prefix?: string;
  remap?: any;
  removeDay?: boolean;
  removeYear?: boolean;
  rich?: boolean;
  scale?: number;
  show_mini_bar?: boolean;
  stringifyNull?: boolean;
  suffix?: string;
  type?: string;
  view_as?: string | null;
  weekday_enabled?: boolean;
  [key: string]: any;
};

/**
 * Visualization-specific display options. Prefer Metabase defaults unless the
 * user asks for an explicit presentation change; many settings depend on exact
 * result column names.
 */
export type VisualizationSettings = {
  /** Show value labels directly on supported chart marks. */
  "graph.show_values"?: boolean;

  /** Stack compatible series as `stacked` or `normalized`; `null` disables stacking. */
  "stackable.stack_type"?: StackType;

  /** Show aggregate labels for stacked chart segments. */
  "graph.show_stack_values"?: StackValuesDisplay;

  /** Render stacked chart segment labels as raw values or as percentages of the stack. */
  "graph.stack_value_format"?: StackValueFormat;

  /** Limit the number of categories before grouping the rest into an "Other" bucket. */
  "graph.max_categories_enabled"?: boolean;

  /** Maximum number of categories to show before using the "Other" bucket. */
  "graph.max_categories"?: number;

  /** Aggregation used for values that are grouped into the "Other" bucket. */
  "graph.other_category_aggregation_fn"?: "sum" | "avg" | "min" | "max" | "stddev" | "median";

  /** Visible table columns and order, as `{ name, enabled }` entries. */
  "table.columns"?: TableColumnOrderSetting[];

  /**
   * Per-column titles, number or currency formatting, and click behavior.
   * Keys can be modern (`getColumnKey`) or legacy (`getLegacyColumnKey`).
   */
  column_settings?: Record<string, ColumnSettings>;

  /** Override the x-axis label. */
  "graph.x_axis.title_text"?: string;

  /** X-axis scale, such as ordinal, timeseries, histogram, linear, pow, or log. */
  "graph.x_axis.scale"?: XAxisScale;

  /** Hide, compact, or rotate x-axis labels where the visualization supports it. */
  "graph.x_axis.axis_enabled"?: true | false | "compact" | "rotate-45" | "rotate-90";

  /** Let Metabase choose the y-axis bounds automatically. */
  "graph.y_axis.auto_range"?: boolean;

  /** Override the y-axis label. */
  "graph.y_axis.title_text"?: string;

  /** Y-axis numeric scale, such as linear, pow, or log. */
  "graph.y_axis.scale"?: YAxisScale;

  /** Show or hide the y-axis where the visualization supports it. */
  "graph.y_axis.axis_enabled"?: boolean;

  /** Fixed y-axis minimum when auto range is disabled. */
  "graph.y_axis.min"?: number;

  /** Fixed y-axis maximum when auto range is disabled. */
  "graph.y_axis.max"?: number;

  /** Numeric value for the goal line. */
  "graph.goal_value"?: number;

  /** Draw a goal line on supported cartesian charts. */
  "graph.show_goal"?: boolean;

  /** Label for the goal line. */
  "graph.goal_label"?: string;

  /** Add a trend line, best for time-based trends without extra groupings. */
  "graph.show_trendline"?: boolean;

  /** Render compatible series in separate panels instead of one chart. */
  "graph.split_panels"?: boolean;

  /**
   * Result column names used for the x-axis, category, or grouping dimension.
   * Prefer Metabase defaults unless the query needs a specific split.
   */
  "graph.dimensions"?: string[];

  /**
   * Result metric column names to plot. Useful when the query returns multiple
   * numeric columns and Metabase should not infer the metrics.
   */
  "graph.metrics"?: string[];

  /** Per-series labels, colors, and display tweaks. Keys are data-dependent. */
  series_settings?: Record<string, SeriesSettings | undefined>;

  /** Explicit order, labels, colors, and enabled state for breakout series. */
  "graph.series_order"?: SeriesOrderSetting[];

  // Timeline events settings
  "timeline.selected_timeline_ids"?: TimelineId[];
  "timeline.excluded_timeline_event_ids"?: TimelineEventId[];

  /** Result numeric column name used to size scatter plot bubbles. */
  "scatter.bubble"?: string;

  /** Color used for increasing waterfall bars. */
  "waterfall.increase_color"?: string;

  /** Color used for decreasing waterfall bars. */
  "waterfall.decrease_color"?: string;

  /** Color used for the total waterfall bar. */
  "waterfall.total_color"?: string;

  /** Add a final total bar to a waterfall chart. */
  "waterfall.show_total"?: boolean;

  /** Explicit order, labels, colors, and enabled state for funnel steps. */
  "funnel.rows"?: SeriesOrderSetting[];

  /** Conditional formatting rules for table cells. */
  "table.column_formatting"?: ColumnFormattingSetting[];

  /** Pivot column selection. */
  "pivot_table.column_split"?: PivotTableColumnSplitSetting;

  /** Initially collapsed pivot rows. */
  "pivot_table.collapsed_rows"?: PivotTableCollapsedRowsSetting;

  /** Smart-scalar comparison configuration. */
  "scalar.comparisons"?: SmartScalarComparison[];

  /** Result column name to display as the main scalar value. */
  "scalar.field"?: string;

  /** Reverse good/bad direction for scalar comparisons. */
  "scalar.switch_positive_negative"?: boolean;

  /** Use compact formatting for the primary scalar number. */
  "scalar.compact_primary_number"?: boolean;

  /** Show the absolute comparison value next to the percent change in trend cards. */
  "scalar.show_comparison_value"?: boolean;

  /** Segment configuration for scalar visualizations. */
  "scalar.segments"?: ScalarSegment[];

  /** Result column name, or names, used as pie slice dimensions. */
  "pie.dimension"?: string | string[];

  /** Result column name used as the middle ring dimension. */
  "pie.middle_dimension"?: string;

  /** Result column name used as the outer ring dimension. */
  "pie.outer_dimension"?: string;

  /** Explicit pie slice order, labels, colors, and enabled state. */
  "pie.rows"?: PieRow[];

  /** Result numeric column name used as the pie slice value. */
  "pie.metric"?: string;

  /** Sort pie slices by metric value. */
  "pie.sort_rows"?: boolean;

  /** Show the pie legend. */
  "pie.show_legend"?: boolean;

  /** Show the total value in the center of the pie. */
  "pie.show_total"?: boolean;

  /** Show labels on pie slices. */
  "pie.show_labels"?: boolean;

  /** Place percentages in the legend, inside slices, both, or neither. */
  "pie.percent_visibility"?: "off" | "legend" | "inside" | "both";

  /** Percentage decimal precision for pie labels. */
  "pie.decimal_places"?: number;

  /** Group small slices below this threshold into "Other". */
  "pie.slice_threshold"?: number;

  /** Legacy slice color map. Prefer defaults unless exact colors matter. */
  "pie.colors"?: Record<string, string>;

  /** Result column name for the source node. */
  "sankey.source"?: string;

  /** Result column name for the target node. */
  "sankey.target"?: string;

  /** Result numeric column name for the flow value. */
  "sankey.value"?: string;

  /** Sankey node alignment. */
  "sankey.node_align"?: "left" | "right" | "justify";

  /** Show labels on Sankey flow edges. */
  "sankey.show_edge_labels"?: boolean;

  /** Formatting for Sankey edge labels. */
  "sankey.label_value_formatting"?: "auto" | "full" | "compact";

  /** Treemap settings */
  "treemap.grouping"?: string;
  "treemap.sub_grouping"?: string | null;
  "treemap.value"?: string;
  "treemap.rows"?: TreemapRow[];
  "treemap.show_parent_labels"?: boolean;
  "treemap.show_parent_values"?: boolean;
  "treemap.show_leaf_labels"?: boolean;
  "treemap.show_leaf_values"?: boolean;

  /** Box plot whisker calculation, such as Tukey or min/max. */
  "boxplot.whisker_type"?: BoxPlotWhiskerType;

  /** Show no points, outliers only, or all points. */
  "boxplot.points_mode"?: BoxPlotPointsMode;

  /** Show the mean marker. */
  "boxplot.show_mean"?: boolean;

  /** Show median values, all values, or no value labels. */
  "boxplot.show_values_mode"?: BoxPlotShowValuesMode;

  /** Map settings */
  "map.type"?: MapType;
  "map.pin_type"?: PinMapStyle;
  "map.latitude_column"?: string;
  "map.longitude_column"?: string;
  "map.metric_column"?: string;
  "map.center_latitude"?: number;
  "map.center_longitude"?: number;
  "map.zoom"?: number;

  /** Columns selected for custom list view. */
  "list.columns"?: ListViewColumns;

  /** Show or hide the first list item column that renders an image or icon. */
  "list.entity_icon_enabled"?: boolean;

  /** Render an image from an image/avatar URL column instead of the default icon. */
  "list.use_image_column"?: boolean;

  /** Icon used for list entities when an image column is not used. */
  "list.entity_icon"?: IconName | null;

  /** Color used for the list entity icon. */
  "list.entity_icon_color"?: string;

  [key: string]: any;
} & EmbedVisualizationSettings;

export type EmbedVisualizationSettings = {
  iframe?: string;
};

export type CardId = number;

export type ListViewColumns = {
  left: string[];
  right: string[];
  image?: string;
};

export type ScalarSegment = {
  min: number | null;
  max: number | null;
  color: string;
  label?: string;
};

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/click-behavior.ts
// ----------------------------------------------------------------------------

// Used to set values for question filters
// Example: "[\"dimension\",[\"field\",17,null]]"
type StringifiedDimension = string;

export interface ClickBehaviorSource {
  id: ParameterId | StringifiedDimension;
  name: string;
  type: "column" | "parameter";
}

export interface ClickBehaviorDimensionTarget {
  id: StringifiedDimension;
  type: "dimension";
  dimension:
    | ["dimension", FieldReference | null | undefined]
    | ["dimension", FieldReference | null | undefined, DimensionTargetOptions];
}

export interface ClickBehaviorParameterTarget {
  id: ParameterId;
  type: "parameter";
}

export interface ClickBehaviorVariableTarget {
  id: TemplateTagName;
  type: "variable";
}

export type ClickBehaviorTarget =
  | ClickBehaviorDimensionTarget
  | ClickBehaviorParameterTarget
  | ClickBehaviorVariableTarget;

export type ClickBehaviorParameterMapping = Record<
  ParameterId | StringifiedDimension,
  {
    id: ParameterId | StringifiedDimension;
    source: ClickBehaviorSource;
    target: ClickBehaviorTarget;
  }
>;

export interface CrossFilterClickBehavior {
  type: "crossfilter";
  parameterMapping?: ClickBehaviorParameterMapping;
}

export type EntityCustomDestinationClickBehavior =
  | DashboardCustomDestinationClickBehavior
  | QuestionCustomDestinationClickBehavior;

export interface DashboardCustomDestinationClickBehavior {
  type: "link";
  linkType: "dashboard";
  targetId?: DashboardId;
  /**
   * tabId will be undefined when user edits click behavior that
   * was created before we supported links to dashboard tabs.
   */
  tabId?: DashboardTabId;
  parameterMapping?: ClickBehaviorParameterMapping;
  linkTextTemplate?: string;
}

export interface QuestionCustomDestinationClickBehavior {
  type: "link";
  linkType: "question";
  targetId?: CardId;
  parameterMapping?: ClickBehaviorParameterMapping;
  linkTextTemplate?: string;
}

export interface ArbitraryCustomDestinationClickBehavior {
  type: "link";
  linkType: "url";
  linkTemplate: string;
  linkTextTemplate?: string;
}

export interface BaseActionClickBehavior {
  type: "action";
  actionType: string;
}

export interface InsertActionClickBehavior extends BaseActionClickBehavior {
  actionType: "insert";
  tableId: number;
}

export interface UpdateActionClickBehavior extends BaseActionClickBehavior {
  actionType: "update";
  objectDetailDashCardId: number;
}

export interface DeleteActionClickBehavior extends BaseActionClickBehavior {
  actionType: "delete";
  objectDetailDashCardId: number;
}

export type ImplicitActionClickBehavior =
  | InsertActionClickBehavior
  | UpdateActionClickBehavior
  | DeleteActionClickBehavior;

/**
 * This is a bit of a hack to allow us using click behavior code
 * for mapping _explicit_ action parameters. We don't actually use the click behavior though.
 * Remove this type and run the type-check to see the errors.
 */
interface HACK_ExplicitActionClickBehavior {
  type: "action";
}

export type ActionClickBehavior = ImplicitActionClickBehavior | HACK_ExplicitActionClickBehavior;

/**
 * Makes click handler use default drills.
 * This is virtual, i.e. if a card has no clickBehavior,
 * it'd behave as if it's an "actionMenu".
 */
export type ActionMenuClickBehavior = {
  type: "actionMenu";
};

export type CustomDestinationClickBehavior =
  | EntityCustomDestinationClickBehavior
  | ArbitraryCustomDestinationClickBehavior;

export type ClickBehavior =
  | ActionMenuClickBehavior
  | CrossFilterClickBehavior
  | CustomDestinationClickBehavior
  | ActionClickBehavior;

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/collection.ts
// ----------------------------------------------------------------------------

export type CollectionNamespace =
  | null
  | "snippets"
  | "transforms"
  | "analytics"
  | "tenant-specific"
  | "shared-tenant-collection";

// Collection ID can be either a numeric or entity id
export type RegularCollectionId = number | string;

export type CollectionId = RegularCollectionId | "root" | "personal" | "users" | "tenant" | "trash";

export type CollectionContentModel = CollectionItemModel;

export type CollectionAuthorityLevel = "official" | null;

export type CollectionType =
  | "instance-analytics"
  | "trash"
  | "remote-synced"
  | "library"
  | "library-data"
  | "library-metrics"
  | "shared-tenant-collection"
  | "tenant-specific-root-collection"
  | null;

export type LastEditInfo = Pick<UserInfo, "id" | "email" | "first_name" | "last_name"> & {
  timestamp: string;
};

export interface Collection {
  id: CollectionId;
  name: string;
  slug?: string;
  // "" for the default for EE's CUSTOM_INSTANCE_ANALYTICS_COLLECTION_ENTITY_ID
  entity_id?: BaseEntityId | "";
  description: string | null;
  can_write: boolean;
  can_restore: boolean;
  can_delete: boolean;
  archived?: boolean;
  children?: Collection[];
  authority_level?: CollectionAuthorityLevel;
  type?: CollectionType;
  is_remote_synced?: boolean;
  namespace: CollectionNamespace | null;

  parent_id?: CollectionId | null;
  personal_owner_id?: UserId;
  is_personal?: boolean;
  is_sample?: boolean; // true if the collection part of the sample content
  is_library_root?: boolean;

  location: string | null;
  effective_location?: string; // location path containing only those collections that the user has permission to access
  effective_ancestors?: CollectionEssentials[];

  here?: CollectionContentModel[];
  below?: CollectionContentModel[];

  git_sync_enabled?: boolean;

  // Assigned on FE
  originalName?: string;
  path?: CollectionId[] | null;
}

export const COLLECTION_ITEM_MODELS = [
  "card",
  "dataset",
  "metric",
  "dashboard",
  "snippet",
  "collection",
  "indexed-entity",
  "document",
  "table",
  "transform",
  "measure",
  "exploration",
] as const;

export type CollectionItemModel = (typeof COLLECTION_ITEM_MODELS)[number];

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/database.ts
// ----------------------------------------------------------------------------

export type DatabaseId = number;

export type InitialSyncStatus = LongTaskStatus;

export type DatabaseSettings = {
  [key: string]: any;
  "database-enable-actions"?: boolean;
};

export type DatabaseFeature =
  | "actions"
  | "actions/data-editing"
  | "basic-aggregations"
  | "binning"
  | "case-sensitivity-string-filter-options"
  | "convert-timezone"
  | "datetime-diff"
  | "database-replication"
  | "database-routing"
  | "dynamic-schema"
  | "expression-aggregations"
  | "expression-literals"
  | "expressions"
  | "expressions/date"
  | "expressions/datetime"
  | "expressions/integer"
  | "expressions/float"
  | "expressions/text"
  | "expressions/today"
  | "native-parameters"
  | "nested-queries"
  | "standard-deviation-aggregations"
  | "percentile-aggregations"
  | "persist-models"
  | "persist-models-enabled"
  | "regex"
  | "regex/lookaheads-and-lookbehinds"
  | "schemas"
  | "set-timezone"
  | "left-join"
  | "right-join"
  | "inner-join"
  | "full-join"
  | "nested-field-columns"
  | "advanced-math-expressions"
  | "connection-impersonation"
  | "connection-impersonation-requires-role"
  | "native-requires-specified-collection"
  | "window-functions/offset"
  | "distinct-where"
  | "saved-question-sandboxing"
  | "split-part"
  | "collate"
  | "transforms/python"
  | "transforms/table";

export interface Database extends DatabaseData {
  id: DatabaseId;
  is_saved_questions: boolean;
  features?: DatabaseFeature[];
  creator_id?: number;
  timezone?: string;
  native_permissions: "write" | "none";
  transforms_permissions?: "write" | "none";
  initial_sync_status: InitialSyncStatus;
  description?: string;
  caveats?: string;
  points_of_interest?: string;
  created_at: ISO8601Time;
  updated_at: ISO8601Time;
  can_upload: boolean;
  uploads_enabled: boolean;
  uploads_schema_name: string | null;
  uploads_table_prefix: string | null;
  is_audit?: boolean;
  is_attached_dwh?: boolean;
  router_database_id?: number | null;
  router_user_attribute?: string | null;

  // Only appears in  GET /api/database/:id
  "can-manage"?: boolean;
  tables?: Table[];
  // Populated when GET /api/database is called with include=schemas; lists
  // schema names that contain at least one readable table.
  schemas?: SchemaName[];
}

export interface DatabaseData {
  id?: DatabaseId;
  name: string;
  engine: string | undefined;
  /** Hosting provider detected from the connection details, e.g. "AWS RDS" */
  provider_name?: string | null;
  // If current user lacks write permission to database, `details` will be
  // missing in responses from the backend, cf. implementation of
  // [[metabase.models.interface/to-json]] for `:model/Database`:
  details?: Record<string, unknown>;
  write_data_details?: Record<string, unknown> | null;
  admin_details?: Record<string, unknown> | null;
  schedules: DatabaseSchedules;
  auto_run_queries: boolean | null;
  refingerprint: boolean | null;
  cache_ttl: number | null;
  is_sample: boolean;
  is_full_sync: boolean;
  is_on_demand: boolean;
  settings?: DatabaseSettings | null;
}

export interface DatabaseSchedules {
  metadata_sync?: ScheduleSettings;
  cache_field_values?: ScheduleSettings | null;
}

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/dataset.ts
// ----------------------------------------------------------------------------

/**
 * @inline
 */
export type RowValue = string | number | null | boolean | object;

export type RowValues = RowValue[];

export type BinningMetadata = {
  binning_strategy?: "default" | "bin-width" | "num-bins";
  bin_width?: number;
  num_bins?: number;
  max_value?: number;
  min_value?: number;
};

export type AggregationType =
  | "count"
  | "sum"
  | "cum-sum"
  | "cum-count"
  | "distinct"
  | "min"
  | "max"
  | "avg"
  | "median"
  | "stddev";

export interface DatasetColumn {
  id?: FieldId;
  name: string;
  display_name: string;
  description?: string | null;
  source: string;
  database_type?: string;
  active?: boolean;
  entity_id?: string;
  fk_field_id?: number;
  nfc_path?: string[] | null;
  parent_id?: number | null;
  position?: number;

  aggregation_type?: AggregationType;

  coercion_strategy?: string | null;
  visibility_type?: FieldVisibilityType;
  table_id?: TableId;
  // FIXME: this prop does not come from API
  remapped_to_column?: DatasetColumn;
  unit?: DatetimeUnit;
  field_ref?: DimensionReference;
  // Deprecated. Columns from old saved questions might have expression_name, but new columns do not.
  expression_name?: any;
  base_type?: string;
  semantic_type?: string | null;
  remapped_from?: string;
  remapped_to?: string;
  effective_type?: string;
  binning_info?: BinningMetadata | null;
  settings?: ColumnSettings;
  fingerprint?: FieldFingerprint | null;

  // model with customized metadata
  fk_target_field_id?: FieldId | null;
  should_index?: boolean;

  remapping?: Map<RowValue, string | number>;
}

export interface ResultsMetadata {
  columns: Field[];
}

export interface DatasetData {
  rows: RowValues[];
  cols: DatasetColumn[];
  insights?: Insight[] | null;
  results_metadata: ResultsMetadata;
  rows_truncated: number;
  pivot_rows_truncated?: number;
  requested_timezone?: string;
  results_timezone?: string;
  download_perms?: DownloadPermission;
  native_form: {
    query: string;
  };
  is_sandboxed?: boolean;
  "pivot-export-options"?: {
    "show-row-totals"?: boolean;
    "show-column-totals"?: boolean;
  };
  untranslatedRows?: RowValues[];

  sourceRows?: (number | null)[][]; // present in pivoted data
}

export type JsonQuery = DatasetQuery & {
  parameters?: NormalizedQueryParameter[];
  "cache-strategy"?: CacheStrategy & {
    /** An ISO 8601 date */
    "invalidated-at"?: string;
    /** In milliseconds */
    "avg-execution-ms"?: number;
  };
};

export interface Dataset {
  data: DatasetData;
  database_id: DatabaseId;
  row_count: number;
  total_count?: number;
  running_time: number;
  json_query?: JsonQuery;
  error?: DatasetError;
  error_type?: DatasetErrorType;
  error_is_curated?: boolean;
  context?: string;
  status?: string;
  /** In milliseconds */
  average_execution_time?: number;
  /** A date in ISO 8601 format */
  cached?: string;
  /** A date in ISO 8601 format */
  started_at?: string;
}

export type DatasetError =
  | string
  | {
      status: number; // HTTP status code
      data?: unknown;
    };

export type DatasetErrorType = "invalid-query" | "missing-required-parameter" | "missing-required-permissions" | string;

export type SeriesCard = UnsavedCard & Partial<Card>;

export type SingleSeries<C extends SeriesCard = SeriesCard> = {
  card: C;
  /**
   * A record that maps visualizer series keys (in the form of COLUMN_1,
   * COLUMN_2, etc.) to their original values (count, avg, etc.).
   */
  columnValuesMapping?: Record<string, VisualizerColumnValueSource[]>;
} & Pick<Dataset, "error" | "started_at" | "data" | "json_query">;

export type TemplateTagId = string;

export type TemplateTagName = string;

export type TemplateTagType =
  | "card"
  | "text"
  | "number"
  | "date"
  | "boolean"
  | "temporal-unit"
  | "dimension"
  | "snippet"
  | "table";

export interface TemplateTag {
  id: TemplateTagId;
  name: TemplateTagName;
  "display-name": string;
  type: TemplateTagType;
  required?: boolean;
  default?: string | null;

  // Card template specific
  "card-id"?: number;

  // Snippet specific
  "snippet-id"?: number;
  "snippet-name"?: string;

  // Field filter and time grouping specific
  dimension?: LocalFieldReference;
  alias?: string;

  // Field filter specific
  "widget-type"?: string;
  options?: ParameterOptions;

  // Table specific
  "table-id"?: TableId;
  "emit-alias"?: boolean;
}

export type TemplateTags = Record<TemplateTagName, TemplateTag>;

export type TemporalUnit =
  | "minute"
  | "hour"
  | "day"
  | "week"
  | "quarter"
  | "month"
  | "year"
  | "minute-of-hour"
  | "hour-of-day"
  | "day-of-week"
  | "day-of-month"
  | "day-of-year"
  | "week-of-year"
  | "month-of-year"
  | "quarter-of-year";

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/document.ts
// ----------------------------------------------------------------------------

export type DocumentId = number;

export type DocumentContent = JSONContent;

export type Document = {
  id: DocumentId;
  creator: BaseUser;
  document: DocumentContent;
  name: string;
  version: number;
  collection_id: CollectionId | null;
  collection_position?: number | null;
  collection?: Collection | null;
  created_at: string;
  updated_at: string;
  archived: boolean;
  is_remote_synced?: boolean;
  can_delete: boolean;
  can_restore: boolean;
  can_write: boolean;
  creator_id: UserId;
  public_uuid?: string | null;
  made_public_by_id?: UserId | null;
  cards?: Record<number, Card>; // Map of card ID -> card metadata, hydrated for public documents
  view_count?: number;
  exploration_id?: number | null;
  is_placeholder?: boolean;
};

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/embed.ts
// ----------------------------------------------------------------------------

export type EmbeddingType = "static-legacy" | "guest-embed";

export type EmbeddingParameterVisibility = "disabled" | "enabled" | "locked";

export type EmbeddingParameters = Record<string, EmbeddingParameterVisibility>;

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/entity.ts
// ----------------------------------------------------------------------------

export type EntityUuid = string;

export type EntityToken = string;

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/entity-id.ts
// ----------------------------------------------------------------------------

declare const __brand: unique symbol;

type Brand<T, B> = T & { readonly [__brand]: B };

type NanoID = Brand<string, "NanoID">;

export type BaseEntityId = NanoID;

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/field.ts
// ----------------------------------------------------------------------------

export type FieldId = number;

export interface FieldFingerprint {
  global?: FieldGlobalFingerprint;
  type?: FieldTypeFingerprint;
}

export interface FieldGlobalFingerprint {
  "distinct-count"?: number;
  "nil%"?: number;
}

export interface FieldTypeFingerprint {
  "type/Text"?: TextFieldFingerprint;
  "type/Number"?: NumberFieldFingerprint;
  "type/DateTime"?: DateTimeFieldFingerprint;
}

export type TextFieldFingerprint = {
  "average-length": number;
  "percent-email": number;
  "percent-json": number;
  "percent-state": number;
  "percent-url": number;
};

export type NumberFieldFingerprint = {
  avg?: number;
  max?: number;
  min?: number;
  q1?: number;
  q3?: number;
  sd?: number;
};

export type DateTimeFieldFingerprint = {
  earliest: string;
  latest: string;
};

export type FieldVisibilityType = "details-only" | "hidden" | "normal" | "retired" | "sensitive";

type HumanReadableFieldValue = string;

export type RemappedFieldValue = [RowValue, HumanReadableFieldValue];

export type NotRemappedFieldValue = [RowValue];

export type FieldValue = NotRemappedFieldValue | RemappedFieldValue;

export type FieldValuesType = "list" | "search" | "none";

export type FieldDimensionType = "internal" | "external";

export type FieldDimension = {
  id: number;
  type: FieldDimensionType;
  name: string;
  human_readable_field_id?: FieldId;
  human_readable_field?: Field;
};

export interface Field {
  id: FieldId | FieldReference;
  table_id: TableId;
  table?: Table;
  field_ref?: DimensionReference;

  name: string;
  display_name: string;
  description: string | null;

  database_type: string;
  base_type: string;
  effective_type?: string;
  semantic_type: string | null;

  active: boolean;
  visibility_type: FieldVisibilityType;
  preview_display: boolean;
  position: number;

  parent_id?: FieldId | null;
  fk_target_field_id: FieldId | null;
  target?: Field;
  values?: FieldValue[];
  remappings?: FieldValue[];
  settings?: FieldFormattingSettings;

  dimensions?: FieldDimension[];
  name_field?: Field;

  max_value?: number;
  min_value?: number;
  has_field_values: FieldValuesType;
  has_more_values?: boolean;

  caveats?: string | null;
  points_of_interest?: string;

  nfc_path: string[] | null;
  json_unfolding: boolean | null;
  coercion_strategy: string | null;
  fingerprint: FieldFingerprint | null;

  last_analyzed: string;
  created_at: string;
  updated_at: string;
}

export interface FieldFormattingSettings {
  currency?: string;
  number_separators?: string;
}

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/formatting.ts
// ----------------------------------------------------------------------------

export type CurrencyStyle = Intl.NumberFormatOptionsCurrencyDisplay;

export type TimeEnabled = "minutes" | "milliseconds" | "seconds";

export interface TimeOnlyOptions {
  local?: boolean;
  time_enabled?: TimeEnabled | null;
  time_format?: string;
  time_style?: string;
}

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/icon.ts
// ----------------------------------------------------------------------------

/**
 * We need a standalone type to prevent adding the `Icons` object (as a type) into `index.d.ts` of Embedding SDK dist
 */
export type IconName =
  | "add"
  | "add_collection"
  | "add_column"
  | "add_data"
  | "add_folder"
  | "add_list"
  | "add_row"
  | "add_to_dash"
  | "add_comment"
  | "ai"
  | "alert"
  | "alert_filled"
  | "alert_confirm"
  | "app"
  | "archive"
  | "area"
  | "attachment"
  | "arrow_up"
  | "arrow_down"
  | "arrow_left"
  | "arrow_left_to_line"
  | "arrow_right"
  | "arrow_split"
  | "audit"
  | "badge"
  | "ban"
  | "bar"
  | "bell"
  | "bell_12"
  | "birthday"
  | "book_open"
  | "bookmark"
  | "bookmark_filled"
  | "bolt"
  | "bolt_filled"
  | "boxplot"
  | "breakout"
  | "bubble"
  | "broken_link"
  | "burger"
  | "calendar"
  | "camera"
  | "chart_layout_default"
  | "chart_layout_stack"
  | "check"
  | "check_filled"
  | "code_block"
  | "chevrondown"
  | "chevronleft"
  | "chevronright"
  | "chevronup"
  | "click"
  | "clipboard"
  | "clock"
  | "clone"
  | "close"
  | "cloud"
  | "cloud_12"
  | "cloud_filled"
  | "collection2"
  | "compare"
  | "combine"
  | "company"
  | "comment"
  | "currency"
  | "connections"
  | "contract"
  | "copy"
  | "corner_up_right"
  | "curved"
  | "database"
  | "database_routing"
  | "dependencies"
  | "dependent"
  | "data_studio"
  | "dash"
  | "dashboard"
  | "curve"
  | "document"
  | "download"
  | "dyno"
  | "edit_document"
  | "edit_document_outlined"
  | "ellipsis"
  | "embed"
  | "embed_interactive"
  | "embed_modular"
  | "embed_static"
  | "empty"
  | "enter_or_return"
  | "event"
  | "exit"
  | "expand"
  | "expand_arrow"
  | "extract"
  | "eye"
  | "eye_crossed_out"
  | "eye_outline"
  | "factory"
  | "field"
  | "fields"
  | "filter"
  | "filter_plus"
  | "find_replace"
  | "bug"
  | "format_code"
  | "formula"
  | "function"
  | "funnel"
  | "funnel_outline"
  | "folder"
  | "folder_database"
  | "folder_filled"
  | "gauge"
  | "gear"
  | "gear_settings_filled"
  | "gem"
  | "ghost"
  | "globe"
  | "glossary"
  | "git_branch"
  | "grabber"
  | "grid"
  | "grid_2x2"
  | "grid_bordered"
  | "group"
  | "google"
  | "google_drive"
  | "google_sheet"
  | "history"
  | "home"
  | "horizontal_bar"
  | "hourglass"
  | "info"
  | "info_outline"
  | "insight"
  | "int"
  | "io"
  | "join_full_outer"
  | "join_inner"
  | "join_left_outer"
  | "join_right_outer"
  | "index"
  | "key"
  | "label"
  | "layout_grid"
  | "ldap"
  | "learn"
  | "lightbulb"
  | "link"
  | "line"
  | "lines"
  | "lineandbar"
  | "line_style_dashed"
  | "line_style_dotted"
  | "line_style_solid"
  | "list"
  | "location"
  | "lock"
  | "lock_filled"
  | "mail"
  | "mail_at"
  | "mail_filled"
  | "map"
  | "mcp"
  | "medallion"
  | "message_circle"
  | "metabot"
  | "metabot_sad"
  | "metric"
  | "mode"
  | "model"
  | "model_with_badge"
  | "moon"
  | "move"
  | "move_card"
  | "network"
  | "new_folder"
  | "note"
  | "note_12"
  | "notebook"
  | "number"
  | "octagon_alert"
  | "package"
  | "palette"
  | "pause"
  | "pencil"
  | "pencil_lines"
  | "permissions_limited"
  | "person"
  | "pie"
  | "pie_slice"
  | "pin"
  | "pinmap"
  | "pivot_table"
  | "play"
  | "play_outlined"
  | "popover"
  | "popular"
  | "progress"
  | "publish"
  | "published"
  | "pulse"
  | "receipt"
  | "recents"
  | "revert"
  | "sankey"
  | "share"
  | "send"
  | "settings"
  | "smile"
  | "split"
  | "sql"
  | "subscription"
  | "straight"
  | "stepped"
  | "sticky_note"
  | "sort"
  | "sort_arrows"
  | "sum"
  | "sync"
  | "synced_collection"
  | "transform"
  | "question"
  | "quote"
  | "redo"
  | "return"
  | "reference"
  | "refresh"
  | "refresh_downstream"
  | "rename"
  | "repository"
  | "rocket"
  | "ruler"
  | "schema"
  | "search"
  | "search_check"
  | "section"
  | "segment"
  | "shield"
  | "shield_outline"
  | "shield_stroke"
  | "sidebar_closed"
  | "sidebar_open"
  | "slack"
  | "slack_colorized"
  | "sliders"
  | "smartscalar"
  | "snail"
  | "snippet"
  | "sparkles"
  | "star_filled"
  | "star"
  | "stop"
  | "store"
  | "string"
  | "sun"
  | "t-shirt"
  | "tab"
  | "table"
  | "table2"
  | "table_index"
  | "telescope"
  | "text_bold"
  | "text_italic"
  | "text_strike"
  | "thumbs_down"
  | "thumbs_up"
  | "time_history"
  | "trash"
  | "trash_filled"
  | "treemap"
  | "trend_down"
  | "trend_flat"
  | "trend_up"
  | "triangle_left"
  | "triangle_right"
  | "unarchive"
  | "undo"
  | "unknown"
  | "unpin"
  | "unpublish"
  | "unreferenced"
  | "unsubscribe"
  | "upload"
  | "variable"
  | "verified"
  | "official_collection"
  | "open_folder"
  | "ordered_list"
  | "verified_filled"
  | "verified_round"
  | "view_archive"
  | "warning"
  | "warning_round"
  | "warning_round_filled"
  | "warning_triangle_filled"
  | "waterfall"
  | "webhook"
  | "10k"
  | "1m"
  | "zap"
  | "zoom_in"
  | "zoom_out"
  | "scalar"
  | "cake"
  | "external"
  | "collection"
  | "beaker"
  | "test_tube"
  | "eye_filled";

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/index.ts
// ----------------------------------------------------------------------------

// ISO8601 timestamp
export type ISO8601Time = string;

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/index-manager.ts
// ----------------------------------------------------------------------------

export type IndexColumnDirection = "asc" | "desc";

export type IndexColumn = {
  name: string;
  direction?: IndexColumnDirection;
};

export const CLASSICAL_INDEX_KINDS = [
  "btree",
  "hash",
  "gin",
  "gist",
  "brin",
  "spgist",
  "fulltext",
  "spatial",
  "clustered",
  "nonclustered",
  "columnstore",
] as const;

export type ClassicalIndexKind = (typeof CLASSICAL_INDEX_KINDS)[number];

export type ClassicalIndex = {
  kind: ClassicalIndexKind;
  name: string;
  columns: IndexColumn[];
  include?: string[];
  unique?: boolean;
};

export type SortKeyStyle = "compound" | "interleaved";

export type SortKeyIndex = {
  kind: "sortkey";
  style: SortKeyStyle;
  columns: IndexColumn[];
};

export type DistKeyIndex = {
  kind: "distkey";
  column: string;
};

export type ClusteringIndex = {
  kind: "clustering";
  name?: string;
  columns: IndexColumn[];
};

export type OrderByIndex = {
  kind: "order-by";
  columns: IndexColumn[];
};

export const SKIP_INDEX_TYPES = ["minmax", "set", "bloom_filter", "ngrambf_v1", "tokenbf_v1"] as const;

export type SkipIndexType = (typeof SKIP_INDEX_TYPES)[number];

export type SkipIndex = {
  kind: "skip-index";
  name: string;
  columns: IndexColumn[];
  type: SkipIndexType;
  "type-args"?: unknown[];
  granularity?: number;
};

export type StructuredIndex = ClassicalIndex | SortKeyIndex | DistKeyIndex | ClusteringIndex | OrderByIndex | SkipIndex;

export type IndexKind = StructuredIndex["kind"];

export type IndexFieldType = "string" | "boolean" | "select" | "integer" | "columns";

export type IndexFieldOption = {
  name: string;
  value: string;
};

export type IndexField = {
  name: string;
  "display-name": string;
  description?: string;
  type: IndexFieldType;
  required?: boolean;
  directions?: boolean;
  options?: IndexFieldOption[];
};

export type IndexMethodLifecycle = "standalone" | "inline";

export type IndexMethod = {
  lifecycle: IndexMethodLifecycle;
  "display-name": string;
  description?: string;
  fields: IndexField[];
};

export type RequestableIndexes = Partial<Record<IndexKind, IndexMethod>>;

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/insight.ts
// ----------------------------------------------------------------------------

export type InsightExpressionOperator = "+" | "-" | "*" | "/" | "log" | "pow" | "exp";

export type InsightExpressionOperand = "x" | number | InsightExpression;

export type InsightExpression =
  | [InsightExpressionOperator, InsightExpressionOperand, InsightExpressionOperand]
  | [InsightExpressionOperator, InsightExpressionOperand];

export interface Insight {
  col: string;
  unit: DateTimeAbsoluteUnit;

  // Used for trend line on line/area/bar/combo charts.
  // Will try to use "best-fit" expression if it exists,
  // if not the function will be x*slope + offset
  "best-fit"?: InsightExpression;
  offset: number;
  slope: number;

  // Used for smart scalar
  "last-change": number;
  "last-value": number;
  "previous-value": number;
}

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/measure.ts
// ----------------------------------------------------------------------------

export type MeasureId = number;

export interface Measure {
  id: MeasureId;
  name: string;
  description: string | null;
  table_id: TableId;
  table?: Table;
  archived: boolean;
  definition: OpaqueDatasetQuery;
  definition_description?: string;
  revision_message?: string;
  created_at: string;
  creator_id: number;
  creator?: UserInfo;
  updated_at: string;
  result_column_name?: string;
  dimensions?: MetricDimension[];
  dimension_mappings?: DimensionMapping[];
}

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/metric.ts
// ----------------------------------------------------------------------------

export type DimensionId = string;

export type MetricDimensionGroup = {
  id: string;
  type: "main" | "connection";
  display_name: string;
};

export type MetricDimensionSource = {
  type: string;
  "field-id": number;
};

export type MetricDimensionStatus = "status/active" | "status/orphaned";

export type MetricDimension = {
  id: DimensionId;
  name?: string;
  display_name: string;
  description?: string | null;
  effective_type: string;
  semantic_type: string | null;
  has_field_values?: "list" | "search" | "none" | null;
  default?: boolean;
  default_temporal_unit?: TemporalUnit;
  status?: MetricDimensionStatus;
  status_message?: string | null;
  dimension_interestingness?: number | null;
  group?: MetricDimensionGroup;
  sources?: MetricDimensionSource[];
};

export type DimensionMappingTarget = ["field", Record<string, unknown>, number | string];

export type DimensionMapping = {
  dimension_id: DimensionId;
  table_id: number;
  target: DimensionMappingTarget;
};

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/moderation.ts
// ----------------------------------------------------------------------------

export type ModerationReviewStatus = "verified" | null;

export type ModerationReview = {
  status: ModerationReviewStatus;
  moderator_id: number;
  created_at: string;
  most_recent?: boolean;
  user: BaseUser;
};

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/parameters.ts
// ----------------------------------------------------------------------------

export type ParameterId = string;

export interface Parameter extends ParameterValuesConfig {
  id: ParameterId;
  name: string;
  "display-name"?: string;
  type: string;
  slug: string;
  sectionId?: string;
  default?: ParameterValueOrArray | null;
  required?: boolean;
  options?: ParameterOptions;
  filteringParameters?: ParameterId[];
  value?: ParameterValueOrArray | null;
  target?: ParameterTarget;
  temporal_units?: TemporalUnit[];
}

export interface ParameterValuesConfig {
  values_query_type?: ValuesQueryType;
  values_source_type?: ValuesSourceType;
  values_source_config?: ValuesSourceConfig;
  temporal_units?: TemporalUnit[];
  isMultiSelect?: boolean;
}

export type ValuesQueryType = "list" | "search" | "none";

export type ValuesSourceType = null | "card" | "static-list";

export interface ValuesSourceConfig {
  values?: string[] | ParameterValue[];
  card_id?: CardId;
  value_field?: FieldReference;
  label_field?: FieldReference;
}

export type VariableTarget = ["template-tag", string];

export type ParameterVariableTarget = ["variable", VariableTarget];

export type ParameterTextTarget = ["text-tag", string];

export type ParameterTarget = ParameterVariableTarget | ParameterDimensionTarget | ParameterTextTarget;

export type ParameterDimensionTarget = NativeParameterDimensionTarget | StructuredParameterDimensionTarget;

export type DimensionTargetOptions = {
  "stage-number"?: number;
};

export type NativeParameterDimensionTarget =
  | ["dimension", VariableTarget]
  | ["dimension", VariableTarget, DimensionTargetOptions];

export type StructuredParameterDimensionTarget =
  | ["dimension", FieldReference]
  | ["dimension", FieldReference, DimensionTargetOptions];

export type ParameterValueOrArray = string | number | boolean | Array<string | number | boolean | null>;

export type HumanReadableParameterValue = string;

export type NotRemappedParameterValue = [RowValue];

export type RemappedParameterValue = [RowValue, HumanReadableParameterValue];

export type ParameterValue = NotRemappedParameterValue | RemappedParameterValue;

export interface ParameterOptions {
  "case-sensitive": boolean;
}

// The parameter payload attached to query execution requests. `value` is
// undefined when the parameter should fall back to its default.
export type NormalizedQueryParameter = {
  id: ParameterId;
  type: string;
  value?: ParameterValueOrArray | null;
  target?: ParameterTarget;
  options?: ParameterOptions;
};

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/performance.ts
// ----------------------------------------------------------------------------

export type CacheStrategyType =
  | "nocache"
  | "ttl" // aka Adaptive
  | "duration"
  | "schedule"
  | "inherit";

interface CacheStrategyBase {
  type: CacheStrategyType;
}

export enum CacheDurationUnit {
  Hours = "hours",
  Minutes = "minutes",
  Seconds = "seconds",
  Days = "days",
}

export interface AdaptiveStrategy extends CacheStrategyBase {
  type: "ttl";
  multiplier: number;
  min_duration_ms: number;
  min_duration_seconds?: number;
}

export interface DoNotCacheStrategy extends CacheStrategyBase {
  type: "nocache";
}

export interface DurationStrategy extends CacheStrategyBase {
  type: "duration";
  duration: number;
  unit: CacheDurationUnit;
  refresh_automatically: boolean;
}

export interface InheritStrategy extends CacheStrategyBase {
  type: "inherit";
}

export interface ScheduleStrategy extends CacheStrategyBase {
  type: "schedule";
  schedule: string;
  refresh_automatically: boolean;
}

/** Cache invalidation strategy */
export type CacheStrategy =
  | DoNotCacheStrategy
  | AdaptiveStrategy
  | DurationStrategy
  | InheritStrategy
  | ScheduleStrategy;

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/permissions.ts
// ----------------------------------------------------------------------------

export enum DataPermissionValue {
  BLOCKED = "blocked",
  CONTROLLED = "controlled",
  IMPERSONATED = "impersonated",
  LEGACY_NO_SELF_SERVICE = "legacy-no-self-service",
  NO = "no",
  QUERY_BUILDER = "query-builder",
  QUERY_BUILDER_AND_NATIVE = "query-builder-and-native",
  SANDBOXED = "sandboxed",
  UNRESTRICTED = "unrestricted",
  // download specific values
  NONE = "none",
  LIMITED = "limited",
  FULL = "full",
  // details specific values
  YES = "yes",
  // data model specific values
  ALL = "all",
  //collections
  WRITE = "write",
  READ = "read",
  //NONE = "none", //shared with download above
}

export type DownloadPermission = DataPermissionValue.FULL | DataPermissionValue.LIMITED | DataPermissionValue.NONE;

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/query.ts
// ----------------------------------------------------------------------------

export interface NativeQuery {
  query: string;
  "template-tags"?: TemplateTags;
  collection?: string;
}

export interface StructuredDatasetQuery {
  type: "query";
  query: StructuredQuery;

  // Database is null when missing data permissions to the database
  database: DatabaseId | null;
  parameters?: NormalizedQueryParameter[];
}

export interface NativeDatasetQuery {
  type: "native";
  native: NativeQuery;

  // Database is null when missing data permissions to the database
  database: DatabaseId | null;
  parameters?: NormalizedQueryParameter[];
}

export type DatasetQuery = OpaqueDatasetQuery | LegacyDatasetQuery;

export type LegacyDatasetQuery = StructuredDatasetQuery | NativeDatasetQuery;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- used for types
declare const OpaqueDatasetQuerySymbol: unique symbol;

export type OpaqueDatasetQuery = unknown & {
  // TODO (AlexP 10/09/25) -- replace usages of this field with Lib.databaseID and drop it from here
  database: DatabaseId | null;
  _opaque: typeof OpaqueDatasetQuerySymbol;
};

export const dateTimeAbsoluteUnits = ["minute", "hour", "day", "week", "month", "quarter", "year"] as const;

export const dateTimeRelativeUnits = [
  "minute-of-hour",
  "hour-of-day",
  "day-of-week",
  "day-of-month",
  "day-of-year",
  "week-of-year",
  "month-of-year",
  "quarter-of-year",
] as const;

export type DateTimeAbsoluteUnit = (typeof dateTimeAbsoluteUnits)[number];

export type DateTimeRelativeUnit = (typeof dateTimeRelativeUnits)[number];

export type DatetimeUnit = "default" | DateTimeAbsoluteUnit | DateTimeRelativeUnit;

export interface ReferenceOptions {
  binning?: BinningOptions;
  "temporal-unit"?: DatetimeUnit;
  "join-alias"?: string;
  "base-type"?: string;
  "source-field"?: number;
}

type BinningOptions = DefaultBinningOptions | NumBinsBinningOptions | BinWidthBinningOptions;

interface DefaultBinningOptions {
  strategy: "default";
}

interface NumBinsBinningOptions {
  strategy: "num-bins";
  "num-bins": number;
}

interface BinWidthBinningOptions {
  strategy: "bin-width";
  "bin-width": number;
}

type ExpressionName = string;

export type StringLiteral = string;

export type NumericLiteral = number | bigint;

export type BooleanLiteral = boolean;

export type DatetimeLiteral = string;

type Value = null | BooleanLiteral | StringLiteral | NumericLiteral | DatetimeLiteral;

type OrderableValue = NumericLiteral | DatetimeLiteral;

type RelativeDatetimePeriod = "current" | "last" | "next" | number;

type OffsetOptions = {
  "lib/uuid": string;
  name: string;
  "display-name": string;
};

// "card__4" like syntax meaning a query is using card 4 as a data source
type NestedQueryTableId = string;

type SourceTableId = TableId | NestedQueryTableId;

export type StructuredQuery = {
  // `null` is the legacy MBQL marker for a not-yet-selected source table
  "source-table"?: SourceTableId | null;
  "source-query"?: StructuredQuery;
  aggregation?: AggregationClause;
  breakout?: BreakoutClause;
  filter?: FilterClause;
  joins?: JoinClause;
  "order-by"?: OrderByClause;
  limit?: LimitClause;
  expressions?: ExpressionClause;
  fields?: FieldsClause;
};

export type AggregationClause = Aggregation[];

type CountAgg = ["count"];

type CountFieldAgg = ["count", ConcreteFieldReference];

type AvgAgg = ["avg", ConcreteFieldReference];

type MedianAgg = ["median", ConcreteFieldReference];

type CumSumAgg = ["cum-sum", ConcreteFieldReference];

type DistinctAgg = ["distinct", ConcreteFieldReference];

type StdDevAgg = ["stddev", ConcreteFieldReference];

type SumAgg = ["sum", ConcreteFieldReference];

type MinAgg = ["min", ConcreteFieldReference];

type MaxAgg = ["max", ConcreteFieldReference];

type OffsetAgg = ["offset", OffsetOptions, Aggregation, NumericLiteral];

type CommonAggregation =
  | CountAgg
  | CountFieldAgg
  | AvgAgg
  | MedianAgg
  | CumSumAgg
  | DistinctAgg
  | StdDevAgg
  | SumAgg
  | MinAgg
  | MaxAgg
  | OffsetAgg;

export type MetricAgg = ["metric", CardId];

export type MeasureAgg = ["measure", { "display-name"?: string }, CardId] | ["measure", CardId];

type InlineExpressionAgg = ["aggregation-options", CommonAggregation, { name: string; "display-name": string }];

/**
 * An aggregation MBQL clause
 */
export type Aggregation = CommonAggregation | MetricAgg | MeasureAgg | InlineExpressionAgg;

type BreakoutClause = Breakout[];

export type Breakout = ConcreteFieldReference;

type FilterClause = Filter;

export type Filter = FieldFilter | CompoundFilter | NotFilter | SegmentFilter;

type AndFilter = ["and", ...Filter[]];

type OrFilter = ["or", ...Filter[]];

type CompoundFilter = AndFilter | OrFilter;

export type FieldFilter =
  | EqualityFilter
  | ComparisonFilter
  | BetweenFilter
  | StringFilter
  | NullFilter
  | NotNullFilter
  | EmptyFilter
  | NotEmptyFilter
  | InsideFilter
  | TimeIntervalFilter;

type NotFilter = ["not", Filter];

type EqualityFilter = ["=" | "!=", ConcreteFieldReference, Value];

export type ComparisonFilter = ["<" | "<=" | ">=" | ">", ConcreteFieldReference, OrderableValue];

type BetweenFilter = ["between", ConcreteFieldReference, OrderableValue, OrderableValue];

type StringFilter =
  | ["starts-with" | "contains" | "does-not-contain" | "ends-with", ConcreteFieldReference, StringLiteral]
  | [
      "starts-with" | "contains" | "does-not-contain" | "ends-with",
      ConcreteFieldReference,
      StringLiteral,
      StringFilterOptions,
    ];

type StringFilterOptions = {
  "case-sensitive"?: false;
};

type NullFilter = ["is-null", ConcreteFieldReference];

type NotNullFilter = ["not-null", ConcreteFieldReference];

type EmptyFilter = ["is-empty", ConcreteFieldReference];

type NotEmptyFilter = ["not-empty", ConcreteFieldReference];

type InsideFilter = [
  "inside",
  ConcreteFieldReference,
  ConcreteFieldReference,
  NumericLiteral,
  NumericLiteral,
  NumericLiteral,
  NumericLiteral,
];

type TimeIntervalFilter =
  | ["time-interval", ConcreteFieldReference, RelativeDatetimePeriod, DateTimeAbsoluteUnit]
  | ["time-interval", ConcreteFieldReference, RelativeDatetimePeriod, DateTimeAbsoluteUnit, TimeIntervalFilterOptions];

type TimeIntervalFilterOptions = {
  "include-current"?: boolean;
};

export type SegmentFilter = ["segment", SegmentId];

type OrderByClause = Array<OrderBy>;

export type OrderBy = ["asc" | "desc", FieldReference];

export type JoinStrategy = "left-join" | "right-join" | "inner-join" | "full-join";

export type JoinAlias = string;

export type JoinCondition = ["=", FieldReference, FieldReference];

export type JoinFields = "all" | "none" | JoinedFieldReference[];

type JoinClause = Array<Join>;

export type Join = {
  "source-table"?: TableId;
  "source-query"?: StructuredQuery;
  condition: JoinCondition;
  alias?: JoinAlias;
  ident?: string;
  strategy?: JoinStrategy;
  fields?: JoinFields;
};

type LimitClause = number;

export type FieldReference = ConcreteFieldReference | AggregateFieldReference;

export type ConcreteFieldReference =
  | LocalFieldReference
  | FieldLiteral
  | ForeignFieldReference
  | JoinedFieldReference
  | ExpressionReference
  | DatetimeField
  | BinnedField;

export type LocalFieldReference = ["field", FieldId, ReferenceOptions | null];

export type ForeignFieldReference = [
  "field",
  FieldId | string,
  ReferenceOptions & { "source-field": FieldId | string },
];

export type ExpressionReference = ["expression", ExpressionName, (ReferenceOptions | null)?];

export type FieldLiteral = ["field", string, ReferenceOptions & { "base-type": string }];

export type JoinedFieldReference = ["field", FieldId | string, ReferenceOptions & { "join-alias": string }];

type DatetimeField = ["field", FieldId | string, Omit<ReferenceOptions, "binning"> & { "temporal-unit": DatetimeUnit }];

export type BinnedField = [
  "field",
  FieldId | string,
  Omit<ReferenceOptions, "temporal-unit"> & {
    binning: BinningOptions;
  },
];

export type AggregateFieldReference = ["aggregation", number, ReferenceOptions | null] | ["aggregation", number];

export type ExpressionClause = {
  [key: ExpressionName]: Expression;
};

export type Expression =
  | NumericLiteral
  | StringLiteral
  | BooleanLiteral
  | OffsetExpression
  | CaseOrIfExpression
  | CallExpression
  | ConcreteFieldReference
  | Filter
  | ValueExpression;

export type CallOptions = { [key: string]: unknown };

export type CallExpression =
  | [ExpressionOperator, ...ExpressionOperand[]]
  | [ExpressionOperator, ...ExpressionOperand[], CallOptions];

export type CaseOperator = "case";

export type IfOperator = "if";

export type CaseOrIfOperator = CaseOperator | IfOperator;

export type CaseOptions = { default?: Expression };

export type CaseOrIfExpression =
  | [CaseOrIfOperator, [Expression, Expression][]]
  | [CaseOrIfOperator, [Expression, Expression][], CaseOptions];

export type ValueExpression = ["value", Value, CallOptions | null];

export type OffsetExpression = ["offset", OffsetOptions, Expression, NumericLiteral];

export type ExpressionOperator = string;

export type ExpressionOperand = Expression | CallOptions;

type FieldsClause = ConcreteFieldReference[];

export type TagName = string;

export type TemplateTagReference = ["template-tag", TagName];

export type DimensionReferenceWithOptions = FieldReference | ExpressionReference | AggregateFieldReference;

export type DimensionReference = DimensionReferenceWithOptions | TemplateTagReference;

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/search.ts
// ----------------------------------------------------------------------------

const ENABLED_SEARCH_MODELS = [
  "collection",
  "dashboard",
  "card",
  "dataset",
  "metric",
  "measure",
  "database",
  "table",
  "action",
  "indexed-entity",
  "document",
  "exploration",
  "transform",
] as const;

export const SEARCH_MODELS = [...ENABLED_SEARCH_MODELS, "segment"] as const;

export type SearchModel = (typeof SEARCH_MODELS)[number];

export type CollectionEssentials = Pick<Collection, "id" | "name" | "authority_level" | "type" | "archived"> &
  Partial<Pick<Collection, "effective_ancestors">>;

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/segment.ts
// ----------------------------------------------------------------------------

export type SegmentId = number;

export interface Segment {
  id: SegmentId;
  name: string;
  description: string;
  table_id: TableId;
  table?: Table;
  archived: boolean;
  caveats?: string;
  points_of_interest?: string;
  // Backend always returns MBQL5 format
  definition: DatasetQuery;
  definition_description: string;
  revision_message?: string;
  created_at: string;
  creator_id: number;
  creator?: UserInfo;
  updated_at: string;
}

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/settings.ts
// ----------------------------------------------------------------------------

export interface ScheduleSettings {
  schedule_type: ScheduleType;
  schedule_day?: ScheduleDayType | null;
  schedule_frame?: ScheduleFrameType | null;
  schedule_hour?: number | null;
  schedule_minute?: number | null;
}

export type ScheduleType = "hourly" | "daily" | "weekly" | "monthly";

export type ScheduleDayType = "sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat";

export type ScheduleFrameType = "first" | "mid" | "last";

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/table.ts
// ----------------------------------------------------------------------------

export type ConcreteTableId = number;

export type VirtualTableId = string;

export type TableId = ConcreteTableId | VirtualTableId;

export type TableVisibilityType = null | "hidden" | "retired" | "sensitive" | "technical" | "cruft";

export type TableDataLayer = "hidden" | "internal" | "final";

export type TableDataSource = "ingested" | "transform" | "metabase-transform" | "source-data" | "upload";

export type TableFieldOrder = "database" | "alphabetical" | "custom" | "smart";

export type Table = {
  id: TableId;
  type?: CardType;
  name: string;
  display_name: string;
  description: string | null;
  entity_type?: string | null;

  db_id: DatabaseId;
  db?: Database;

  schema: SchemaName;

  fks?: ForeignKey[];
  fields?: Field[];
  segments?: Segment[];
  measures?: Measure[];
  metrics?: Card[];
  field_order: TableFieldOrder;

  active: boolean;
  visibility_type: TableVisibilityType;
  initial_sync_status: InitialSyncStatus;
  is_upload: boolean;
  is_writable?: boolean;
  caveats?: string;
  points_of_interest?: string;
  created_at: string;
  updated_at: string;

  data_source: TableDataSource | null;
  data_layer: TableDataLayer | null;
  owner_email: string | null;
  owner_user_id: UserId | null;
  owner?: TableOwner | { email: string } | null;
  estimated_row_count?: number | null;
  transform_id: TransformId | null; // readonly
  view_count: number;
  transform?: Transform | null;

  collection_id: CollectionId | null;
  is_published: boolean;
  collection?: Collection;
};

export type TableOwner = Pick<UserInfo, "id" | "email" | "first_name" | "last_name">;

export type SchemaName = string;

export interface ForeignKey {
  origin?: Field;
  origin_id: FieldId;
  destination?: Field;
  destination_id: FieldId;
  relationship: "Mt1";
}

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/timeline.ts
// ----------------------------------------------------------------------------

export type TimelineId = number;

export type TimelineEventId = number;

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/transform.ts
// ----------------------------------------------------------------------------

export type TransformId = number;

export type TransformTagId = number;

export type TransformRunId = number;

export type TransformOwner = Pick<UserInfo, "id" | "email" | "first_name" | "last_name">;

export type TransformType = "native" | "python" | "mbql";

export type Transform = {
  id: TransformId;
  name: string;
  description: string | null;
  source: TransformSource;
  source_type: TransformType;
  target: TransformTarget;
  collection_id: CollectionId | null;
  created_at: string;
  updated_at: string;
  source_readable: boolean;
  can_read?: boolean;
  can_write?: boolean;
  can_execute?: boolean;

  source_database_id?: DatabaseId | null;

  // true when transform was deleted but still referenced by runs
  deleted?: boolean;

  // creator fields
  creator_id?: UserId;

  // owner fields (can be different from creator)
  owner_user_id?: UserId | null;
  owner_email?: string | null;
  owner?: TransformOwner | null;

  last_checkpoint_value?: string | null;

  // set by the job transforms endpoint on transforms pulled into the plan
  // only as dependencies (not tagged for the job); `scheduled` says whether
  // any active job's schedule covers them
  dependency?: boolean;
  scheduled?: boolean;

  // hydrated fields
  collection?: Collection | null;
  tag_ids?: TransformTagId[];
  table?: Table | null;
  last_run?: TransformRun | null;
  creator?: UserInfo;
  requestable_indexes?: RequestableIndexes | null;
};

export type PythonTransformTableEntry = {
  alias: string;
  table_id: ConcreteTableId;
  schema?: SchemaName | null;
  database_id: DatabaseId;
};

export type PythonTransformTableAliases = PythonTransformTableEntry[];

export type LookbackUnit = "millisecond" | "second" | DateTimeAbsoluteUnit;

// Only supported for temporal checkpoint columns.
export type TransformLookback = {
  value: number;
  unit: LookbackUnit;
};

export type TransformSourceCheckpointStrategy = {
  type: "checkpoint";
  "checkpoint-filter-field-id": number;
  lookback?: TransformLookback | null;
};

export type SourceIncrementalStrategy = TransformSourceCheckpointStrategy;

export type PythonTransformSource = {
  type: "python";
  body: string;
  "source-database": DatabaseId;
  "source-tables": PythonTransformTableAliases;
  "source-incremental-strategy"?: SourceIncrementalStrategy;
};

export type QueryTransformSource = {
  type: "query";
  query: DatasetQuery;
  "source-incremental-strategy"?: SourceIncrementalStrategy;
};

export type TransformSource = QueryTransformSource | PythonTransformSource;

export type TransformTargetAppendStrategy = {
  type: "append";
};

export type MergeKeyColumn = {
  name?: string;
  "field-id"?: number | null;
};

export type TransformTargetMergeStrategy = {
  type: "merge";
  "unique-key": MergeKeyColumn[];
};

export type TargetIncrementalStrategy = TransformTargetAppendStrategy | TransformTargetMergeStrategy;

export type TableTarget = {
  type: "table";
  name: string;
  schema: SchemaName | null;
  database: DatabaseId;
};

export type TableIncrementalTarget = {
  type: "table-incremental";
  name: string;
  schema: SchemaName | null;
  database: DatabaseId;
  "target-incremental-strategy": TargetIncrementalStrategy;
};

export type TransformTarget = TableTarget | TableIncrementalTarget;

export type TransformRun = {
  id: TransformRunId;
  status: TransformRunStatus | null;
  start_time: string;
  end_time: string | null;
  message: string | null;
  run_method: TransformRunMethod;

  checkpoint_filter_field_id?: number | null;
  checkpoint_lo_value?: string | null;
  checkpoint_hi_value?: string | null;

  // hydrated fields
  transform?: Transform;
};

export const TRANSFORM_RUN_STATUSES = ["started", "succeeded", "failed", "timeout", "canceling", "canceled"] as const;

export type TransformRunStatus = (typeof TRANSFORM_RUN_STATUSES)[number];

export const TRANSFORM_RUN_METHODS = ["manual", "cron"] as const;

export type TransformRunMethod = (typeof TRANSFORM_RUN_METHODS)[number];

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/user.ts
// ----------------------------------------------------------------------------

export type UserId = number;

export interface BaseUser {
  id: UserId;
  first_name: string | null;
  last_name: string | null;
  common_name: string;
  email: string;
  locale: string | null;
  is_active: boolean;
  is_qbnewb: boolean;
  is_superuser: boolean;
  is_data_analyst: boolean;

  date_joined: string;
  last_login: string;
  first_login: string;
  updated_at: string;
  tenant_id: number | null;
}

// Used when hydrating `creator` property
export type UserInfo = Pick<
  BaseUser,
  | "id"
  | "common_name"
  | "first_name"
  | "last_name"
  | "email"
  | "date_joined"
  | "last_login"
  | "is_superuser"
  | "is_qbnewb"
  | "is_active"
>;

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/util.ts
// ----------------------------------------------------------------------------

export type LongTaskStatus = "incomplete" | "complete" | "aborted";

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/visualization.ts
// ----------------------------------------------------------------------------

export const virtualCardDisplayTypes = ["action", "heading", "link", "placeholder", "text", "iframe"] as const;

export type VirtualCardDisplay = (typeof virtualCardDisplayTypes)[number];

export const cardDisplayTypes = [
  "table",
  "bar",
  "line",
  "pie",
  "scalar",
  "row",
  "area",
  "combo",
  "pivot",
  "smartscalar",
  "gauge",
  "progress",
  "funnel",
  "object",
  "map",
  "scatter",
  "boxplot",
  "waterfall",
  "sankey",
  "treemap",
  "list",
] as const;

export type CardDisplayType = (typeof cardDisplayTypes)[number];

export type CustomVizDisplayType = `custom:${string}`;

export type QueryVisualizationDisplayType = CardDisplayType | CustomVizDisplayType;

export type VisualizationDisplay =
  | VirtualCardDisplay
  | QueryVisualizationDisplayType
  // internal-only display registered by the audit app, not saveable on cards
  | "audit-table";

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/visualization-settings.ts
// ----------------------------------------------------------------------------

// SmartScalar (Trend Chart)
export type SmartScalarComparisonType =
  | "anotherColumn"
  | "previousValue"
  | "previousPeriod"
  | "periodsAgo"
  | "staticNumber";

interface BaseSmartScalarComparison {
  id: string; // client-side generated, used for sorting
  type: SmartScalarComparisonType;
}

export interface SmartScalarComparisonAnotherColumn extends BaseSmartScalarComparison {
  type: "anotherColumn";
  column: string;
  label: string;
}

export interface SmartScalarComparisonPeriodsAgo extends BaseSmartScalarComparison {
  type: "periodsAgo";
  value: number;
}

export interface SmartScalarComparisonPreviousPeriod extends BaseSmartScalarComparison {
  type: "previousPeriod";
}

export interface SmartScalarComparisonPreviousValue extends BaseSmartScalarComparison {
  type: "previousValue";
}

export interface SmartScalarComparisonStaticNumber extends BaseSmartScalarComparison {
  type: "staticNumber";
  value: number;
  label: string;
}

export type SmartScalarComparison =
  | SmartScalarComparisonAnotherColumn
  | SmartScalarComparisonPreviousValue
  | SmartScalarComparisonPreviousPeriod
  | SmartScalarComparisonPeriodsAgo
  | SmartScalarComparisonStaticNumber;

export interface PieRow {
  key: string;
  name: string;
  originalName: string;
  color: string;
  /** Palette color the slice was given, so it can follow the active palette */
  color_name?: string;
  defaultColor: boolean;
  enabled: boolean;
  hidden: boolean;
  isOther: boolean;
}

export interface TreemapRow {
  key: string;
  name: string;
  originalName: string;
  color: string;
  /** Palette color the group was given, so it can follow the active palette */
  color_name?: string;
  defaultColor: boolean;
  enabled: boolean;
  hidden: boolean;
}

// ----------------------------------------------------------------------------
// frontend/src/metabase-types/api/visualizer.ts
// ----------------------------------------------------------------------------

export type VisualizerDataSourceType = "card";

export type VisualizerDataSourceId = `${VisualizerDataSourceType}:${number}`;

export type VisualizerColumnReference = {
  sourceId: VisualizerDataSourceId;
  name: string; // in combined dataset
  originalName: string; // in original dataset
};

export type VisualizerDataSourceNameReference = `$_${VisualizerDataSourceId}_name`;

export type VisualizerColumnValueSource = VisualizerColumnReference | VisualizerDataSourceNameReference;

export type VisualizerVizDefinition = {
  display: VisualizationDisplay | null;
  columnValuesMapping: Record<string, VisualizerColumnValueSource[]>;
  settings: VisualizationSettings;
};
